import { useState, useEffect, useMemo, useCallback } from 'react'
import { Upload, Loader2, ArrowLeft } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Upload as AntUpload, notification } from 'antd'
import { formatStartDate, formatEndDate } from '@/utils/format'
import type { PaginationState } from '@tanstack/react-table'
import { getCoreRowModel, getPaginationRowModel } from '@tanstack/react-table'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination'
import { ConfirmModal } from '@/components/ui/confirm-modal'
import { PageHeader } from '@/components/ui/page-header'
import { LibrarySidebar } from './LibrarySidebar'
import { KnowledgeFilterPanel } from './LibraryFilterPanel'
import type { KnowledgeFilterValues } from './LibraryFilterPanel'
import { buildColumns } from './columns'
import {
  fetchCategories,
  fetchKnowledgeItems,
  deleteCategory,
  deleteKnowledgeItem,
  uploadKnowledgeItem,
} from '@/api/library.api'
import type {
  LibraryCategory,
  KnowledgeItem,
  PaginatedResponse,
} from '@/types/api'
import { CategoryFormDrawer } from './LibraryFormDrawer'
import { usePermission } from '@/hooks/use-permission'
import { useAtomValue } from 'jotai'
import { currentUserAtom } from '@/store/auth.atom'
import { exportKb, useTableExport } from '@/api/dowload-excel.api'
import type { ExportPayload } from '@/api/dowload-excel.api'

const DEFAULT_FILTER: KnowledgeFilterValues = {
  keySearch: '',
  time: undefined,
}

export function LibraryPage() {
  const queryClient = useQueryClient()
  const hasDocUpload = usePermission('doc-upload')
  const hasKbCreate = usePermission('kb-create')
  const hasDocDelete = usePermission('doc-delete')
  const hasKbDelete = usePermission('kb-delete')
  const hasDocExport = usePermission('doc-export')
  const hasKbExport = usePermission('kb-export')
  const canUpload = hasDocUpload || hasKbCreate
  const canDeleteDoc = hasDocDelete || hasKbDelete
  const canExport = hasDocExport || hasKbExport
  const currentUser = useAtomValue(currentUserAtom)
  const showUnitColumn = currentUser?.userType === 1

  const [activeCategory, setActiveCategory] = useState<LibraryCategory | null>(
    null,
  )
  const activeCategoryId = activeCategory?.id || null

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [showFilter, setShowFilter] = useState(false)
  const [filter, setFilter] = useState<KnowledgeFilterValues>(DEFAULT_FILTER)
  const [activeFilters, setActiveFilters] =
    useState<KnowledgeFilterValues>(DEFAULT_FILTER)
  const [View, setView] = useState<'sidebar' | 'content'>('sidebar')
  const [isExporting, setIsExporting] = useState(false)

  // --- Drawer States ---
  const [isCategoryDrawerOpen, setIsCategoryDrawerOpen] = useState(false)
  const [categoryFormMode, setCategoryFormMode] = useState<'create' | 'update'>(
    'create',
  )
  const [editingCategory, setEditingCategory] =
    useState<LibraryCategory | null>(null)

  // --- Delete Modal States ---
  const [deletingCategory, setDeletingCategory] =
    useState<LibraryCategory | null>(null)
  const [deletingItem, setDeletingItem] = useState<KnowledgeItem | null>(null)

  // --- Queries ---
  const [catPageIndex, setCatPageIndex] = useState(0)
  const [catPageSize] = useState(10)
  const [catSearchTerm, setCatSearchTerm] = useState('')

  const { data: categoryData } = useQuery({
    queryKey: ['libraryCategories', catPageIndex, catPageSize, catSearchTerm],
    queryFn: ({ signal }) =>
      fetchCategories(
        catPageIndex,
        catPageSize,
        catSearchTerm,
        !showUnitColumn ? currentUser?.unitId : undefined,
        signal,
      ),
    refetchOnMount: 'always',
  })

  const categories = categoryData?.items || []
  const catTotalPages = categoryData?.totalPages || 1

  useEffect(() => {
    if (!activeCategory && categories.length > 0) {
      setActiveCategory(categories[0])
    }
    if (
      activeCategory &&
      categories.length > 0 &&
      !categories.find((c) => c.id === activeCategory.id)
    ) {
      setActiveCategory(categories[0])
    }
  }, [categories, activeCategory])

  const apiParams = useMemo(() => {
    return {
      fileName: activeFilters.keySearch || undefined,
      fromDate: formatStartDate(activeFilters.time?.from) || undefined,
      toDate: formatEndDate(activeFilters.time?.to) || undefined,
    }
  }, [activeFilters])

  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      'knowledge',
      activeCategoryId,
      pageIndex,
      pageSize,
      apiParams.fileName,
      apiParams.fromDate,
      apiParams.toDate,
    ],
    queryFn: ({ signal }) => {
      if (!activeCategoryId)
        return Promise.reject(new Error('No category selected'))

      return fetchKnowledgeItems(
        activeCategoryId,
        pageIndex,
        pageSize,
        apiParams,
        signal,
      )
    },
    enabled: !!activeCategoryId,
    refetchOnMount: 'always',
  })

  const hasData = Boolean(data?.items && data.items.length > 0)

  // --- Mutations ---
  const deleteCategoryMutation = useMutation({
    mutationFn: (id: string) => deleteCategory(id),
    onSuccess: () => {
      notification.success({ message: 'Xóa thư viện thành công!' })

      queryClient
        .invalidateQueries({ queryKey: ['libraryCategories'] })
        .then(() => {
          const updatedCategories =
            queryClient.getQueryData<PaginatedResponse<LibraryCategory>>([
              'libraryCategories',
              catPageIndex,
              catPageSize,
              catSearchTerm,
            ])?.items || []

          if (activeCategory?.id === deletingCategory?.id) {
            if (updatedCategories.length > 0) {
              setActiveCategory(updatedCategories[0])
            } else {
              setActiveCategory(null)
            }
          }
        })
      setDeletingCategory(null)
    },
  })

  const deleteItemMutation = useMutation({
    mutationFn: (item: KnowledgeItem) =>
      deleteKnowledgeItem(activeCategoryId!, item.id),
    onSuccess: () => {
      notification.success({ message: 'Xóa tài liệu thành công!' })

      const currentData = queryClient.getQueryData<
        PaginatedResponse<KnowledgeItem>
      >(['knowledge', activeCategoryId, pageIndex, pageSize, apiParams])

      if (currentData?.items.length === 1 && pageIndex > 0) {
        setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex - 1 }))
      } else {
        queryClient.invalidateQueries({ queryKey: ['knowledge'] })
      }
      setDeletingItem(null)
    },
  })

  const uploadItemMutation = useMutation({
    mutationFn: async (files: File[]) => {
      if (!activeCategoryId) throw new Error('Chưa chọn thư viện')

      return uploadKnowledgeItem(activeCategoryId, files)
    },
    onSuccess: () => {
      notification.success({ title: 'Tải lên tài liệu thành công!' })
      queryClient.invalidateQueries({ queryKey: ['knowledge'] })
    },
  })

  const columns = useMemo(
    () =>
      buildColumns({
        pageIndex,
        pageSize,
        canDelete: canDeleteDoc,
        onDelete: (item) => setDeletingItem(item),
      }),
    [pageIndex, pageSize, canDeleteDoc],
  )

  // Callback gọi API xuất Excel
  const handleExportApi = useCallback(
    async (payload: ExportPayload) => {
      if (!activeCategoryId) return
      try {
        setIsExporting(true)
        await exportKb(
          {
            kbId: activeCategoryId,
            page: pageIndex,
            size: pageSize,
            ...apiParams,
          },
          payload,
          `Danh_sach_tai_lieu_${activeCategory?.name || 'thu_vien'}`,
        )
      } finally {
        setIsExporting(false)
      }
    },
    [activeCategoryId, activeCategory?.name, pageIndex, pageSize, apiParams],
  )

  const { handleExport } = useTableExport(
    `Danh sách tài liệu - ${activeCategory?.name || 'Thư viện'}`,
    columns,
    handleExportApi,
  )

  const tableHeader = (
    <div className="flex flex-col gap-2 py-1">
      <PageHeader
        title={activeCategory?.name || 'Tên kiến thức'}
        onToggleFilter={() => setShowFilter((v) => !v)}
        onExport={canExport ? handleExport : undefined}
        disabled={!hasData}
        isExporting={isExporting}
        titlePrefix={
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 lg:hidden"
            onClick={() => setView('sidebar')}
          >
            <ArrowLeft size={20} />
          </Button>
        }
      >
        {canUpload && (
          <AntUpload
            multiple
            showUploadList={false}
            accept=".docx,.pdf,.txt,.md"
            className="flex-1 sm:flex-none [&>.ant-upload]:w-full"
            beforeUpload={(file, fileList) => {
              if (fileList.indexOf(file) === fileList.length - 1) {
                uploadItemMutation.mutate(fileList)
              }
              return false
            }}
          >
            <Button
              variant="primary"
              size="md"
              className="h-10 w-full gap-2"
              disabled={!activeCategoryId || uploadItemMutation.isPending}
            >
              {uploadItemMutation.isPending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Upload size={16} />
              )}
              {uploadItemMutation.isPending ? 'Đang tải...' : 'Tải lên'}
            </Button>
          </AntUpload>
        )}
      </PageHeader>

      {showFilter && (
        <KnowledgeFilterPanel
          values={filter}
          onChange={setFilter}
          onSearch={() => {
            const trimmedFilter = {
              ...filter,
              keySearch: filter.keySearch.trim(),
            }
            setFilter(trimmedFilter)
            setActiveFilters(trimmedFilter)
            setPagination((s) => ({ ...s, pageIndex: 0 }))
            refetch()
          }}
        />
      )}
    </div>
  )

  return (
    <section className="flex h-[calc(100vh-90px)] min-h-0 flex-col">
      <div className="flex items-start justify-between">
        <h1 className="text-[20px] font-bold uppercase text-gray-900">
          QUẢN LÝ THƯ VIỆN
        </h1>
      </div>

      <div className="flex min-h-0 flex-1 gap-4 pb-2">
        <div
          className={`h-full w-full shrink-0 lg:w-auto ${
            View === 'content' ? 'hidden lg:block' : 'block'
          }`}
        >
          <LibrarySidebar
            categories={categories}
            activeCategoryId={activeCategoryId}
            searchTerm={catSearchTerm}
            onSearchChange={(val) => {
              setCatSearchTerm(val)
              setCatPageIndex(0)
            }}
            pageIndex={catPageIndex}
            totalPages={catTotalPages}
            onPageChange={setCatPageIndex}
            onSelectCategory={(id) => {
              const foundCategory = categories.find((c) => c.id === id) || null
              if (foundCategory) {
                setActiveCategory(foundCategory)
              }
              setPagination({ pageIndex: 0, pageSize: 10 })
              setView('content')
            }}
            onAddCategory={() => {
              setCategoryFormMode('create')
              setEditingCategory(null)
              setIsCategoryDrawerOpen(true)
            }}
            onRenameCategory={(cat) => {
              setCategoryFormMode('update')
              setEditingCategory(cat)
              setIsCategoryDrawerOpen(true)
            }}
            onDeleteCategory={(cat) => setDeletingCategory(cat)}
          />
        </div>

        <div
          className={`min-h-0 min-w-0 flex-1 flex-col rounded-2xl bg-white p-4 pt-0 ${
            View === 'sidebar' ? 'hidden lg:flex' : 'flex'
          }`}
        >
          {isLoading ? (
            <div className="flex h-full flex-col rounded-xl border border-[#E5E7EB] bg-white p-4">
              {tableHeader}
              <div className="flex flex-1 flex-col items-center justify-center gap-2">
                <Loader2 size={32} className="animate-spin text-gray-400" />
                <span className="text-sm text-gray-500">
                  Đang tải dữ liệu...
                </span>
              </div>
            </div>
          ) : (
            <div className="min-h-0 flex-1 w-full overflow-x-auto">
              <DataTable
                columns={columns}
                data={data?.items ?? []}
                headertable={tableHeader}
                className="flex h-full min-h-0 flex-col min-w-[600px] md:min-w-full [&_thead_tr]:!bg-[#F1F3F5]"
                tableOptions={{
                  state: { pagination: { pageIndex, pageSize } },
                  onPaginationChange: setPagination,
                  getPaginationRowModel: getPaginationRowModel(),
                  getCoreRowModel: getCoreRowModel(),
                  manualPagination: true,
                  pageCount: data?.totalPages,
                }}
                footer={
                  <Pagination
                    page={pageIndex + 1}
                    pageCount={data?.totalPages ?? 1}
                    pageSize={pageSize}
                    totalCount={data?.total ?? 0}
                    onPageChange={(p) =>
                      setPagination((s) => ({ ...s, pageIndex: p - 1 }))
                    }
                    onPageSizeChange={(size) =>
                      setPagination({ pageIndex: 0, pageSize: size })
                    }
                  />
                }
              />
            </div>
          )}
        </div>
      </div>

      <CategoryFormDrawer
        isOpen={isCategoryDrawerOpen}
        mode={categoryFormMode}
        category={editingCategory}
        onClose={() => setIsCategoryDrawerOpen(false)}
      />

      <ConfirmModal
        isOpen={!!deletingCategory}
        title="Xóa thư viện"
        description={
          <>
            Bạn có chắc chắn muốn xóa thư viện{' '}
            <strong>{deletingCategory?.name}</strong> này?
          </>
        }
        variant="danger"
        isLoading={deleteCategoryMutation.isPending}
        onClose={() => setDeletingCategory(null)}
        onConfirm={() =>
          deletingCategory && deleteCategoryMutation.mutate(deletingCategory.id)
        }
      />

      <ConfirmModal
        isOpen={!!deletingItem}
        title="Xóa tài liệu"
        description={
          <>
            Bạn có chắc chắn muốn xóa tài liệu{' '}
            <strong>{deletingItem?.name}</strong> này?
          </>
        }
        variant="danger"
        isLoading={deleteItemMutation.isPending}
        onClose={() => setDeletingItem(null)}
        onConfirm={() =>
          deletingItem && deleteItemMutation.mutate(deletingItem)
        }
      />
    </section>
  )
}
